import {
    createContext,
    useContext,
    useState,
    useCallback,
    useRef,
    type ReactNode,
} from 'react'
import type { Building, Block } from '../utils/types'
import { api } from '../services/API'
import { toArray } from '../utils/toArray'
import { useCondominium } from './CondominioContext'
import { useNotification } from './NotificationContext'
import axios from 'axios'
import i18n from '../i18n/i18n'


interface BuildingContextValue {
    buildings: Building[]
    blocks: Block[]
    condominiumBlocks: Block[]
    getBuildingsByCondominium: (condominioId: string) => Promise<Building[]>
    getBlocksByBuilding: (edificioId: string) => Promise<Block[]>
    getBlocksByCondominium: (condominioId: string) => Promise<Block[]>
    createBuilding: (condominioId: string, nome: string, totalUnidades?: number, totalAndares?: number) => Promise<Building>
    createBlock: (condominioId: string, edificioId: string | null, nome: string, descricao: string | null) => Promise<Block>
    /** Descarta a cache de edifícios (de um condomínio, ou toda). Chamar depois de criar/editar/arquivar/remover. */
    invalidateBuildings: (condominioId?: string) => void
    /** Descarta a cache de blocos (por condomínio e por edifício). */
    invalidateBlocks: () => void
}

const BuildingContext = createContext<BuildingContextValue | null>(null)

// Edifícios e blocos mudam pouco e são pedidos ao montar de vários ecrãs
// (Unidades, Condóminos, formulários…). Uma cache curta em memória evita repetir
// o mesmo GET a cada navegação; a invalidação explícita nas mutações mantém-na
// coerente, e o TTL cobre alterações feitas noutro sítio.
const CACHE_TTL_MS = 60_000

interface CacheEntry<T> {
    data: T[]
    ts: number
}

export function BuildingProvider({ children }: { children: ReactNode }) {
    const { activeCondominium } = useCondominium()
    const { notify } = useNotification()
    const [buildings, setBuildings] = useState<Building[]>([])
    const [blocks, setBlocks] = useState<Block[]>([])
    const [condominiumBlocks, setCondominiumBlocks] = useState<Block[]>([])

    const buildingsCache = useRef(new Map<string, CacheEntry<Building>>())
    const blocksByCondominiumCache = useRef(new Map<string, CacheEntry<Block>>())
    const blocksByBuildingCache = useRef(new Map<string, CacheEntry<Block>>())

    const fresh = <T,>(entry: CacheEntry<T> | undefined): entry is CacheEntry<T> =>
        !!entry && Date.now() - entry.ts < CACHE_TTL_MS

    const invalidateBuildings = useCallback((condominioId?: string) => {
        if (condominioId) buildingsCache.current.delete(condominioId)
        else buildingsCache.current.clear()
    }, [])

    const invalidateBlocks = useCallback(() => {
        blocksByCondominiumCache.current.clear()
        blocksByBuildingCache.current.clear()
    }, [])

    const getBuildingsByCondominium = useCallback(
        async (condominioId: string) => {
            if (activeCondominium?.id === condominioId && !activeCondominium.temPredios) return []
            const cached = buildingsCache.current.get(condominioId)
            if (fresh(cached)) {
                setBuildings(cached.data)
                return cached.data
            }
            try {
                const response = await api.get(`/api/edificios/condominio/${condominioId}`);

                const data: Building[] = toArray(response.data)
                buildingsCache.current.set(condominioId, { data, ts: Date.now() })
                setBuildings(data)
                return data
            } catch (error) {
                if (axios.isAxiosError(error) && error.response?.status === 403) notify(i18n.t('condominiums:form.featureNotEnabled'), 'error')
                throw error
            }
        },
        [activeCondominium, notify]
    )

    const getBlocksByCondominium = useCallback(
        async (condominioId: string) => {
            if (activeCondominium?.id === condominioId && !activeCondominium.temBlocos) return []
            const cached = blocksByCondominiumCache.current.get(condominioId)
            if (fresh(cached)) {
                setCondominiumBlocks(cached.data)
                return cached.data
            }
            try {
                const response = await api.get(`/api/blocos/condominio/${condominioId}`)
            const data: Block[] = toArray(response.data)
            blocksByCondominiumCache.current.set(condominioId, { data, ts: Date.now() })
            setCondominiumBlocks(data)
            return data
            } catch (error) {
                if (axios.isAxiosError(error) && error.response?.status === 403) notify(i18n.t('condominiums:form.featureNotEnabled'), 'error')
                throw error
            }
        },
        [activeCondominium, notify]
    )

    const getBlocksByBuilding = useCallback(
        async (edificioId: string) => {
            if (!activeCondominium?.temBlocos) return []
            const cached = blocksByBuildingCache.current.get(edificioId)
            if (fresh(cached)) {
                setBlocks(cached.data)
                return cached.data
            }
            try {
                const response = await api.get(`/api/blocos/edificio/${edificioId}`)

                const data: Block[] = toArray(response.data)
                blocksByBuildingCache.current.set(edificioId, { data, ts: Date.now() })
                setBlocks(data)
                return data
            } catch (error) {
                if (axios.isAxiosError(error) && error.response?.status === 403) notify(i18n.t('condominiums:form.featureNotEnabled'), 'error')
                throw error
            }
        },
        [activeCondominium?.temBlocos, notify]
    )

    const createBuilding = useCallback(
        async (condominioId: string, nome: string, totalUnidades?: number, totalAndares?: number): Promise<Building> => {
            if (activeCondominium?.id === condominioId && !activeCondominium.temPredios) {
                const error = new Error(i18n.t('condominiums:form.featureNotEnabled'))
                notify(error.message, 'error')
                throw error
            }
            try {
                const response = await api.post('/api/edificios', { estateId: condominioId, nome, totalUnidades, totalAndares })

                const newItem: Building = response.data
                setBuildings((prev) => [...prev, newItem])
                buildingsCache.current.delete(condominioId)
                return newItem
            } catch (error) {
                if (axios.isAxiosError(error) && error.response?.status === 403) notify(i18n.t('condominiums:form.featureNotEnabled'), 'error')
                throw error
            }
        }, [activeCondominium, notify])

    const createBlock = useCallback(async (condominioId: string, edificioId: string | null, nome: string, descricao: string | null): Promise<Block> => {
        if (activeCondominium?.id === condominioId && !activeCondominium.temBlocos) {
            const error = new Error(i18n.t('condominiums:form.featureNotEnabled'))
            notify(error.message, 'error')
            throw error
        }
        try {
            const response = await api.post('/api/blocos', { estateId: condominioId, edificioId, nome, descricao })

            const newItem: Block = response.data
            setBlocks((prev) => [...prev, newItem])
            invalidateBlocks()
            return newItem
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 403) notify(i18n.t('condominiums:form.featureNotEnabled'), 'error')
            throw error
        }
    }, [activeCondominium, notify, invalidateBlocks])

    return (
        <BuildingContext.Provider
            value={{
                buildings,
                blocks,
                condominiumBlocks,
                getBuildingsByCondominium,
                getBlocksByBuilding,
                getBlocksByCondominium,
                createBuilding,
                createBlock,
                invalidateBuildings,
                invalidateBlocks,
            }}
        >
            {children}
        </BuildingContext.Provider>
    )
}

export function useBuildings() {
    const ctx = useContext(BuildingContext)
    if (!ctx) {
        throw new Error('useBuildings deve ser usado dentro de um <BuildingProvider>.')
    }
    return ctx
}
